import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-42028a10"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "bg-white p-4 rounded-lg mt-4 text-left text-textPrimary" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "text-md font-bold" }, "Tipo de cálculo do pagamento", -1))
const _hoisted_3 = { class: "flex flex-col gap-3 mt-4" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "text-[#2F7F91] font-bold mr-1" }, "R$", -1))
const _hoisted_5 = { class: "grid grid-cols-2 gap-4 mt-6" }
const _hoisted_6 = { class: "text-left" }
const _hoisted_7 = { class: "text-md font-bold" }
const _hoisted_8 = { class: "text-left" }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-md font-bold" }, "Custo base", -1))
const _hoisted_10 = { class: "text-left" }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-md font-bold" }, "Custo excepcional", -1))
const _hoisted_12 = { class: "text-left" }
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-md font-bold" }, "Prejuízo previsto", -1))

import { ref, computed, watch, type PropType } from 'vue'
import { mdiLoading } from '@mdi/js'
import GenericInput from '../Inputs/GenericInput.vue'
import RadioButton from '../RadioButton/index.vue'
import {
  calculateExceptionalCost,
  calculatePredictedResult,
  calculeteBaseCost,
  calculeteTotalAgreedValue
} from '@/utils/math'
import type { SimulateFinancialsResult } from '@/Network/Types/Requests/DoctorStationExceptionRequests'

// Local Intl formatter for currency to ensure off-mode parity
const VALOR_POR_HORA = 'Valor por hora'

export default /*#__PURE__*/_defineComponent({
  __name: 'AgreedValue',
  props: {
  baseDuration: { type: Number, required: true, default: 0 },
  exceptionalDuration: { type: Number, required: true, default: 1 },
  baseValue: { type: Number, required: true, default: 0 },
  paymentModality: { type: String, required: true, default: 'no fluxo' },
  overrideFinancials: {
    type: Object as PropType<SimulateFinancialsResult | null>,
    required: false,
    default: undefined
  },
  isOverrideLoading: { type: Boolean, required: false, default: false },
  hasOverrideError: { type: Boolean, required: false, default: false }
},
  emits: ['update:agreedValue'],
  setup(__props, { emit }) {

const props = __props;

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const formatBRLOrDash = (v: string | number | null | undefined): string =>
  v === null || v === undefined ? '—' : brl.format(Number(v))





const paymentType = ref(VALOR_POR_HORA)
const inputValue = ref(0)

const paymentTypeLabel = computed(() => {
  return paymentType.value === VALOR_POR_HORA ? 'Valor por plantão' : 'Valor por hora'
})

const inputLabel = computed(() => {
  return paymentType.value === VALOR_POR_HORA ? 'Valor/hora' : 'Valor total do plantão'
})

const tax = computed(() => {
  if (props.paymentModality === 'à vista') {
    const feeFromEnv = process.env.VUE_APP_EXCEPTIONAL_REQUEST_FEE_PAYMENT_ADVANCE
    const fee = Number(feeFromEnv)
    return isNaN(fee) ? 0.1 : fee
  }
  return 0
})

const agreedHourlyRate = computed(() => {
  if (paymentType.value === VALOR_POR_HORA) {
    return inputValue.value || 0
  }

  if (props.exceptionalDuration > 0) {
    const totalValue = inputValue.value || 0
    const duration = props.exceptionalDuration

    const rawRate = totalValue / duration

    const safeRate = Math.round(rawRate * 100) / 100

    return safeRate
  }

  return 0
})

const totalAgreedValue = computed(() => {
  return calculeteTotalAgreedValue(agreedHourlyRate.value, props.exceptionalDuration)
})

const calculatedDisplayValue = computed(() => {
  if (paymentType.value === 'Valor por plantão') {
    return agreedHourlyRate.value
  }
  return totalAgreedValue.value
})

const baseCost = computed(() => {
  return calculeteBaseCost(props.baseDuration, props.exceptionalDuration, props.baseValue)
})

const exceptionalCost = computed(() => {
  return calculateExceptionalCost(
    props.exceptionalDuration,
    props.baseDuration,
    agreedHourlyRate.value,
    tax.value
  )
})

const predictedResult = computed(() => {
  return calculatePredictedResult(exceptionalCost.value, baseCost.value, props.exceptionalDuration)
})

// --- Override (simulate) — só ativo quando o caller (wizard) fornece
// overrideFinancials !== undefined. undefined => comportamento 100% local,
// idêntico ao pré-existente (usado por NewExceptionalityRequest.vue).
const isOverrideActive = computed(() => props.overrideFinancials !== undefined)

const displayTotalValue = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.totalExceptionalValue ?? null
  return calculatedDisplayValue.value
})

const displayBaseCost = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.baseCost ?? null
  return baseCost.value
})

const displayExceptionalCost = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.exceptionalCost ?? null
  return exceptionalCost.value
})

const displayPredictedResult = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.estimatedLoss ?? null
  return predictedResult.value
})

const predictedResultColorClass = computed(() => {
  if (displayPredictedResult.value === null) return ''
  return Number(displayPredictedResult.value) >= 0 ? 'text-positive' : 'text-negative'
})

watch(
  [paymentType, inputValue, tax],
  () => {
    emit('update:agreedValue', {
      type: paymentType.value,
      value: inputValue.value,
      results: {
        total: totalAgreedValue.value,
        baseCost: baseCost.value,
        exceptionalCost: exceptionalCost.value,
        result: predictedResult.value
      }
    })
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createVNode(RadioButton, {
        id: "Valor por hora",
        name: "radio-group",
        value: "Valor por hora",
        label: "Valor por hora",
        modelValue: paymentType.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((paymentType).value = $event))
      }, null, 8, ["modelValue"]),
      _createVNode(RadioButton, {
        id: "Valor por plantão",
        name: "radio-group",
        value: "Valor por plantão",
        label: "Valor por plantão",
        modelValue: paymentType.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((paymentType).value = $event))
      }, null, 8, ["modelValue"])
    ]),
    _createVNode(GenericInput, {
      label: inputLabel.value,
      class: "h-12 mt-4",
      modelValue: inputValue.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((inputValue).value = $event)),
      type: "number",
      min: "0"
    }, {
      "prepend-inner": _withCtx(() => [
        _hoisted_4
      ]),
      _: 1
    }, 8, ["label", "modelValue"]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("h3", _hoisted_7, _toDisplayString(paymentTypeLabel.value), 1),
        _createElementVNode("p", {
          class: _normalizeClass(["mt-1 text-sm flex items-center gap-1", { 'opacity-50': __props.isOverrideLoading }])
        }, [
          (__props.isOverrideLoading)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 0,
                icon: _unref(mdiLoading),
                size: "14",
                class: "animate-spin text-primary"
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(__props.hasOverrideError ? '—' : formatBRLOrDash(displayTotalValue.value)), 1)
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_8, [
        _hoisted_9,
        _createElementVNode("p", {
          class: _normalizeClass(["mt-1 text-sm flex items-center gap-1", { 'opacity-50': __props.isOverrideLoading }])
        }, [
          (__props.isOverrideLoading)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 0,
                icon: _unref(mdiLoading),
                size: "14",
                class: "animate-spin text-primary"
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(__props.hasOverrideError ? '—' : formatBRLOrDash(displayBaseCost.value)), 1)
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_10, [
        _hoisted_11,
        _createElementVNode("p", {
          class: _normalizeClass(["mt-1 text-sm flex items-center gap-1", { 'opacity-50': __props.isOverrideLoading }])
        }, [
          (__props.isOverrideLoading)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 0,
                icon: _unref(mdiLoading),
                size: "14",
                class: "animate-spin text-primary"
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(__props.hasOverrideError ? '—' : formatBRLOrDash(displayExceptionalCost.value)), 1)
        ], 2)
      ]),
      _createElementVNode("div", _hoisted_12, [
        _hoisted_13,
        _createElementVNode("p", {
          class: _normalizeClass(["mt-1 text-sm flex items-center gap-1", [predictedResultColorClass.value, { 'opacity-50': __props.isOverrideLoading }]])
        }, [
          (__props.isOverrideLoading)
            ? (_openBlock(), _createBlock(_component_v_icon, {
                key: 0,
                icon: _unref(mdiLoading),
                size: "14",
                class: "animate-spin text-primary"
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, _toDisplayString(__props.hasOverrideError ? '—' : formatBRLOrDash(displayPredictedResult.value)), 1)
        ], 2)
      ])
    ])
  ]))
}
}

})